import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building2, Users, Handshake, DollarSign, Percent, TrendingUp,
  ArrowLeft, RefreshCw, FileText, Briefcase, UserCheck,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import { API, useAuth } from "../../App";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { FilterBar } from "@/components/admin/FilterBar";
import { MultiSelectFilter } from "@/components/admin/MultiSelectFilter";
import { useUrlFilters } from "@/hooks/useUrlFilters";
import { useFilterOptions } from "@/hooks/useFilterOptions";

/**
 * Corporate Executive Dashboard (Phase 2).
 * KPI cards + trend charts fed by GET /api/partners/dashboard/executive.
 */

const BRAND = "#DE6E4B";
const NAVY = "#0A2342";
const PIE_COLORS = ["#DE6E4B", "#0A2342", "#7C3AED", "#059669", "#F59E0B", "#3B82F6", "#EC4899", "#64748B"];

export default function PartnersDashboard() {
  const { token } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { options: enums } = useFilterOptions();

  const FILTER_CONFIG = useMemo(() => ({
    industry: { type: "multi" },
    partner_type: { type: "multi" },
    submarket: { type: "multi" },
    assigned_agent: { type: "multi" },
    company_id: { type: "multi" },
    company_status: { type: "multi" },
    created_from: { type: "string" },
    created_to: { type: "string" },
  }), []);
  const { filters, setFilter, reset, toParams, activeCount } = useUrlFilters(
    "partners-dashboard", FILTER_CONFIG,
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/partners/dashboard/executive`, {
        headers: { Authorization: `Bearer ${token}` },
        params: toParams(),
      });
      setData(res.data);
    } catch (err) {
      toast({ title: "Failed to load dashboard", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [token, toParams, toast]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  if (loading && !data) {
    return <div className="p-8 text-slate-400" data-testid="partners-dashboard-loading">Loading executive dashboard…</div>;
  }
  if (!data) {
    return <div className="p-8 text-slate-400">No dashboard data.</div>;
  }

  const { cards, charts } = data;

  return (
    <div className="max-w-7xl mx-auto" data-testid="partners-dashboard-page">
      <Link
        to="/admin/partners"
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#DE6E4B] mb-4"
        data-testid="partners-dashboard-back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Companies
      </Link>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-[#DE6E4B]" />
            Corporate Executive Dashboard
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            Partnership pipeline health — referrals, conversions, leases, and commission.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="partners-dashboard-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Global filter bar */}
      <FilterBar
        hideSearch
        onReset={reset}
        activeCount={activeCount}
        testId="dashboard-filters"
      >
        <MultiSelectFilter
          label="Industry"
          options={enums?.industries || []}
          value={filters.industry || []}
          onChange={(v) => setFilter("industry", v)}
          testId="dashboard-filter-industry"
        />
        <MultiSelectFilter
          label="Partner Type"
          options={enums?.partner_types || []}
          value={filters.partner_type || []}
          onChange={(v) => setFilter("partner_type", v)}
          testId="dashboard-filter-partner-type"
        />
        <MultiSelectFilter
          label="Submarket"
          options={enums?.houston_submarkets || []}
          value={filters.submarket || []}
          onChange={(v) => setFilter("submarket", v)}
          testId="dashboard-filter-submarket"
        />
        <MultiSelectFilter
          label="Company"
          options={(enums?.companies || []).map((c) => ({ value: c.id, label: c.name }))}
          value={filters.company_id || []}
          onChange={(v) => setFilter("company_id", v)}
          testId="dashboard-filter-company"
        />
        <MultiSelectFilter
          label="Status"
          options={enums?.company_statuses || []}
          value={filters.company_status || []}
          onChange={(v) => setFilter("company_status", v)}
          testId="dashboard-filter-status"
        />
        <MultiSelectFilter
          label="Agent"
          options={enums?.assigned_agents || []}
          value={filters.assigned_agent || []}
          onChange={(v) => setFilter("assigned_agent", v)}
          testId="dashboard-filter-agent"
        />
        <div className="flex items-center gap-1" data-testid="dashboard-filter-date">
          <span className="text-xs text-slate-500">Referrals</span>
          <input
            type="date"
            value={filters.created_from || ""}
            onChange={(e) => setFilter("created_from", e.target.value)}
            className="h-9 px-2 text-sm border border-slate-300 rounded-md"
            data-testid="dashboard-filter-created-from"
          />
          <span className="text-xs text-slate-400">–</span>
          <input
            type="date"
            value={filters.created_to || ""}
            onChange={(e) => setFilter("created_to", e.target.value)}
            className="h-9 px-2 text-sm border border-slate-300 rounded-md"
            data-testid="dashboard-filter-created-to"
          />
        </div>
      </FilterBar>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="partners-dashboard-kpis">
        <KpiCard icon={Building2} label="Companies" value={cards.companies} testId="kpi-companies" />
        <KpiCard icon={UserCheck} label="Contacts" value={cards.contacts} testId="kpi-contacts" />
        <KpiCard icon={Briefcase} label="Active Partners" value={cards.active_partners} testId="kpi-active-partners" />
        <KpiCard icon={Handshake} label="New Referrals" value={cards.new_referrals} testId="kpi-new-referrals" />
        <KpiCard icon={Users} label="Converted Leads" value={cards.converted_leads} testId="kpi-converted-leads" />
        <KpiCard icon={Users} label="Active Leads" value={cards.active_leads} testId="kpi-active-leads" />
        <KpiCard icon={FileText} label="Closed Leases" value={cards.closed_leases} highlight testId="kpi-closed-leases" />
        <KpiCard icon={DollarSign} label="Total Commission" value={`$${(cards.total_commission || 0).toLocaleString()}`} highlight testId="kpi-total-commission" />
        <KpiCard icon={Percent} label="Conversion Rate" value={`${cards.conversion_rate}%`} testId="kpi-conversion-rate" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Monthly Referrals (6 mo)" testId="chart-monthly-referrals">
          {charts.monthly_referrals.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={charts.monthly_referrals} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" name="Referrals" stroke={BRAND} strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Referral Funnel" testId="chart-referral-funnel">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts.referral_funnel} layout="vertical" margin={{ top: 8, right: 24, bottom: 0, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="stage" width={72} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" name="Referrals" fill={NAVY} radius={[0, 6, 6, 0]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top Companies by Referrals" testId="chart-top-companies">
          {charts.top_companies.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={charts.top_companies} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="company_name" tick={{ fontSize: 11 }} interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="referrals" name="Referrals" fill={BRAND} radius={[6, 6, 0, 0]} barSize={36} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Referrals by Industry" testId="chart-referrals-by-industry">
          {charts.referrals_by_industry.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={charts.referrals_by_industry}
                  dataKey="count"
                  nameKey="industry"
                  cx="50%" cy="50%"
                  innerRadius={50} outerRadius={85}
                  paddingAngle={2}
                >
                  {charts.referrals_by_industry.map((entry, i) => (
                    <Cell key={entry.industry} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Commission by Company" testId="chart-commission-by-company">
          {charts.commission_by_company.length === 0 ? (
            <EmptyChart message="No commission recorded yet — commissions appear once partner-referred leases are signed." />
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={charts.commission_by_company} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="company_name" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toLocaleString()}`} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                <Bar dataKey="commission" name="Commission" fill="#059669" radius={[6, 6, 0, 0]} barSize={36} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Top Recruiters" testId="chart-top-recruiters">
          {charts.top_recruiters.length === 0 ? (
            <EmptyChart message="No contact-linked referrals yet." />
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="top-recruiters-list">
              {charts.top_recruiters.map((r, i) => (
                <li key={r.contact_id} className="py-2.5 flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-orange-50 text-[#DE6E4B] text-xs font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 text-sm truncate">{r.name || "Unknown"}</div>
                  </div>
                  <span className="text-sm font-bold text-slate-700">{r.referrals} referral{r.referrals === 1 ? "" : "s"}</span>
                </li>
              ))}
            </ul>
          )}
        </ChartCard>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, highlight, testId }) {
  return (
    <div
      className={`rounded-lg p-4 border ${highlight ? "bg-orange-50 border-orange-100" : "bg-white border-slate-200"}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
        <Icon className={`w-4 h-4 ${highlight ? "text-[#DE6E4B]" : "text-slate-400"}`} />
        {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${highlight ? "text-[#DE6E4B]" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid={testId}>
      <h3 className="font-semibold text-slate-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function EmptyChart({ message = "No data yet." }) {
  return <div className="h-[240px] flex items-center justify-center text-sm text-slate-400">{message}</div>;
}
